import React, { useState, useEffect, useCallback } from "react";
import {
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
  RefreshCw,
  Save,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { MARKETING_API_URL } from "../../constants";

// Fields the PATCH endpoint accepts, used to diff the form against the row.
const EDITABLE_FIELDS = [
  "description",
  "draft_model",
  "escalation_model",
  "system_prompt",
  "self_check_prompt",
  "confidence_threshold",
  "max_revisions",
  "max_output_tokens",
  "temperature",
  "active",
];

const NUMERIC_FIELDS = {
  temperature: { min: 0, max: 2, step: 0.05 },
  confidence_threshold: { min: 0, max: 1, step: 0.05 },
  max_revisions: { min: 0, max: 3, step: 1 },
  max_output_tokens: { min: 256, max: 32768, step: 256 },
};

const toForm = (task) => {
  const form = {};
  EDITABLE_FIELDS.forEach((f) => {
    form[f] = task[f] === null ? "" : task[f];
  });
  return form;
};

const TaskEditor = ({ task, token, onSaved, onError }) => {
  const [form, setForm] = useState(() => toForm(task));
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null); // null = closed
  const [loadingHistory, setLoadingHistory] = useState(false);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const changes = () => {
    const out = {};
    EDITABLE_FIELDS.forEach((f) => {
      let value = form[f];
      if (f === "self_check_prompt" && value === "") value = null;
      if (f in NUMERIC_FIELDS && value !== "") value = Number(value);
      const original = task[f];
      if (JSON.stringify(value) !== JSON.stringify(original)) out[f] = value;
    });
    return out;
  };

  const dirty = Object.keys(changes()).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${MARKETING_API_URL}/admin/inference-tasks/${task.task_type}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(changes()),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Save failed (${res.status})`);
      onSaved(data);
    } catch (err) {
      onError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleHistory = async () => {
    if (history !== null) {
      setHistory(null);
      return;
    }
    setLoadingHistory(true);
    try {
      const res = await fetch(
        `${MARKETING_API_URL}/admin/inference-tasks/${task.task_type}/history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setHistory(data.history || []);
    } catch {
      setHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  return (
    <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <input
          type="text"
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          System Prompt
        </label>
        <textarea
          value={form.system_prompt}
          onChange={(e) => set("system_prompt", e.target.value)}
          rows={10}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Self-Check Prompt{" "}
          <span className="font-normal text-gray-500">
            (empty = skip the self-check step)
          </span>
        </label>
        <textarea
          value={form.self_check_prompt}
          onChange={(e) => set("self_check_prompt", e.target.value)}
          rows={4}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Draft Model
          </label>
          <input
            type="text"
            value={form.draft_model}
            onChange={(e) => set("draft_model", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Escalation Model
          </label>
          <input
            type="text"
            value={form.escalation_model}
            onChange={(e) => set("escalation_model", e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono"
          />
        </div>
        {Object.entries(NUMERIC_FIELDS).map(([field, cfg]) => (
          <div key={field}>
            <label className="block text-sm font-medium text-gray-700 mb-1 capitalize">
              {field.replace(/_/g, " ")}
            </label>
            <input
              type="number"
              min={cfg.min}
              max={cfg.max}
              step={cfg.step}
              value={form[field]}
              onChange={(e) => set(field, e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
            />
          </div>
        ))}
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={!!form.active}
              onChange={(e) => set("active", e.target.checked)}
            />
            Active
          </label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Changes
        </button>
        {dirty && (
          <button
            onClick={() => setForm(toForm(task))}
            className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <X className="w-4 h-4" /> Discard
          </button>
        )}
        <button
          onClick={toggleHistory}
          className="ml-auto inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          {loadingHistory ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <History className="w-4 h-4" />
          )}
          {history !== null ? "Hide History" : "Edit History"}
        </button>
      </div>

      {history !== null && (
        <div className="border border-gray-200 rounded-md bg-white divide-y divide-gray-100 max-h-96 overflow-y-auto">
          {history.length === 0 && (
            <p className="p-3 text-sm text-gray-500">No edits recorded yet.</p>
          )}
          {history.map((h) => (
            <details key={h.id} className="p-3">
              <summary className="text-sm text-gray-700 cursor-pointer">
                {new Date(h.changed_at).toLocaleString()} — {h.changed_by}
              </summary>
              <pre className="mt-2 p-2 bg-gray-50 rounded text-xs whitespace-pre-wrap text-gray-600">
                {h.previous.system_prompt}
              </pre>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

const InferenceTaskPrompts = () => {
  const { token } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${MARKETING_API_URL}/admin/inference-tasks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load tasks (${res.status})`);
      const data = await res.json();
      setTasks(data.tasks || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const onSaved = (updated) => {
    setTasks((ts) =>
      ts.map((t) => (t.task_type === updated.task_type ? updated : t))
    );
    setNotice(`Saved ${updated.task_type} — previous version kept in edit history.`);
    setError("");
    setTimeout(() => setNotice(""), 5000);
  };

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold text-gray-900">Agent Prompts</h1>
        <button
          onClick={fetchTasks}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">
        System prompts and tuning for the inference router's registered tasks
        (Marketing Director agent). Changes take effect on the next router call —
        no deploy needed. Every save is snapshotted to the edit history.
      </p>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md">
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-md">
          {notice}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading tasks…
        </div>
      ) : tasks.length === 0 && !error ? (
        <div className="p-4 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md">
          No task types registered yet. Tasks are registered via the reviewed
          seed files in <code>marketing/seed/</code> — this page can edit
          existing tasks but not create them.
        </div>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <div
              key={task.task_type}
              className="border border-gray-200 rounded-lg bg-white"
            >
              <button
                onClick={() =>
                  setExpanded(expanded === task.task_type ? null : task.task_type)
                }
                className="w-full flex items-center gap-3 p-4 text-left"
              >
                {expanded === task.task_type ? (
                  <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <span className="font-mono text-sm font-medium text-gray-900">
                    {task.task_type}
                  </span>
                  <p className="text-sm text-gray-500 truncate">
                    {task.description}
                  </p>
                </div>
                <span className="text-xs text-gray-400 shrink-0">
                  {task.draft_model}
                </span>
                <span
                  className={`text-xs px-2 py-1 rounded-full shrink-0 ${
                    task.active
                      ? "bg-green-50 text-green-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {task.active ? "Active" : "Inactive"}
                </span>
              </button>
              {expanded === task.task_type && (
                <TaskEditor
                  key={task.updated_at}
                  task={task}
                  token={token}
                  onSaved={onSaved}
                  onError={(msg) => setError(msg)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InferenceTaskPrompts;
